import { useState } from 'react';
import { Button } from '../atoms/Button/Button';
import { Stack } from '../atoms/Stack/Stack';
import { Section } from '../templates/Section/Section';
import { useTranslation } from '../../i18n/useTranslation';
import { ApiError } from '../../lib/apiClient';
import { dataExportFilename, fetchOwnDataExport, RATE_LIMITED, saveJsonFile } from '../../services/accountService';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';

type Status = 'idle' | 'loading' | 'done' | 'rateLimited' | 'error';

export interface DataExportSectionProps {
  className?: string;
}

/**
 * Settings "Your data": self-service download of all stored personal data as a
 * JSON file (GET /api/v1/me/export, GDPR Art. 15/20). Rendered right above the
 * danger zone, so a copy can be saved before the account is deleted. The
 * outcome is announced in a polite live region; errors are text, not colour only.
 */
export function DataExportSection({ className = '' }: DataExportSectionProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('idle');

  const download = async (): Promise<void> => {
    if (status === 'loading') return;
    setStatus('loading');
    try {
      const data = await fetchOwnDataExport();
      saveJsonFile(data, dataExportFilename());
      setStatus('done');
    } catch (err: unknown) {
      setStatus(err instanceof ApiError && err.code === RATE_LIMITED ? 'rateLimited' : 'error');
    }
  };

  const message =
    status === 'done'
      ? t('settings.dataExportSection.success')
      : status === 'rateLimited'
        ? t('settings.dataExportSection.errors.rateLimited')
        : status === 'error'
          ? t('settings.dataExportSection.errors.generic')
          : '';
  const isError = status === 'rateLimited' || status === 'error';

  return (
    <Section
      title={t('settings.dataExportSection.title')}
      description={t('settings.dataExportSection.description')}
      className={className || undefined}
    >
      <Stack gap="3" align="start">
        <Button variant="secondary" onClick={() => void download()} isLoading={status === 'loading'}>
          {t('settings.dataExportSection.button')}
        </Button>
        {isError && <Alert>{message}</Alert>}
        <LiveRegion>
          {status === 'done' ? (
            <Alert tone="success" live="off" compact>
              {message}
            </Alert>
          ) : null}
        </LiveRegion>
      </Stack>
    </Section>
  );
}

export default DataExportSection;
